import { Box, CircularProgress } from "@mui/material"
import { AddOutlined, UploadFileOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { ReactElement } from "react"
import { useState } from "react"

import { FILE_TYPE_MAPPING } from "../../constants"

import { GoodsReceiptFileFormDialog } from "./GoodsReceiptFileFormDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { PermissionActionType } from "@/constants/permissions"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { GoodsReceiptUploadsTable } from "@/screens/WarehouseReceiving/components/GoodsReceiptUploads/GoodsReceiptFileTable"

export const GoodsReceiptUploadsTab = (): ReactElement => {
  const { goodsReceiptId } = useParams({ strict: false })
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])
  const [isFileFormOpen, setIsFileFormOpen] = useState(false)

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: `${goodsReceiptId}`,
    fieldGroups: Object.keys(FILE_TYPE_MAPPING).map((val) => val as FieldGroupEnum),
    referenceTableName: ReferenceTableName.AdvancedGoodsReceipt,
  })
  const { removeSingleFile: handleFileRemove } = useFilesRemove(refetchFileUploadData)

  const handleOpenFileForm = (): void => {
    setIsFileFormOpen(true)
  }

  let content: ReactElement

  if (fileList?.length) {
    content = (
      <Box className='flex h-full flex-col'>
        {canMaintain && (
          <Button
            color='primary'
            size='small'
            appearance='outlined'
            className='mb-6 self-start'
            startIcon={<UploadFileOutlined />}
            onClick={handleOpenFileForm}
            data-testid='add-file-button'
          >
            Upload
          </Button>
        )}
        <GoodsReceiptUploadsTable data={fileList} isLoading={fileFetching} onRemove={handleFileRemove} />
      </Box>
    )
  } else if (fileFetching) {
    content = (
      <Box className='absolute inset-0 flex items-center justify-center'>
        <CircularProgress size={48} />
      </Box>
    )
  } else {
    content = (
      <InformationMissingAlert
        description='No documents have been uploaded yet. Once you add some, you will be able to see them here.'
        {...(canMaintain && {
          title: "Start by uploading documents",
          action: (
            <Button
              color='inherit'
              size='small'
              appearance='outlined'
              startIcon={<AddOutlined />}
              onClick={handleOpenFileForm}
              data-testid='add-file-button'
            >
              New Upload
            </Button>
          ),
        })}
        data-testid='goods-receipt-file-alert'
      />
    )
  }

  return (
    <>
      {isFileFormOpen && (
        <GoodsReceiptFileFormDialog
          onClose={() => setIsFileFormOpen(false)}
          onRefresh={refetchFileUploadData}
          goodsReceiptId={goodsReceiptId}
        />
      )}
      <Box className='relative h-full' data-testid='goods-receipt-uploads-container'>
        {content}
      </Box>
    </>
  )
}
